import React from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import FlowActionButton from "@components/FlowActionButtons/FlowActionButton";
import UnclaimButton from "@components/FlowActionButtons/UnclaimButton";
import type { Task } from "@services/vesselCall/types";
import { useClaimVesselCall, useUnclaimVesselCall } from "./hooks";
import { canUserUnclaim, TASK_STATUSES } from "./utils";

interface CloseVesselCallButtonProps {
  isVisible: boolean;
  refetchVesselCall: () => void;
  openDrawer: (drawerName: string) => void;
  vesselCallFlowData?: {
    vesselCallId: string;
    task: Task | null;
    status: string;
  };
}

const LANG_BASE = "vessel-call.vessel-call-status-header";
const PERMISSION = "canCloseVesselCall";

const CloseVesselCallButton: React.FC<CloseVesselCallButtonProps> = ({
  isVisible,
  refetchVesselCall,
  openDrawer,
  vesselCallFlowData,
}) => {
  const { onClaimVesselCall } = useClaimVesselCall(
    vesselCallFlowData?.vesselCallId,
    refetchVesselCall,
  );
  const { onUnclaimVesselCall } = useUnclaimVesselCall(
    vesselCallFlowData?.vesselCallId,
    refetchVesselCall,
  );

  if (!isVisible || !vesselCallFlowData?.task) return null;

  const { task } = vesselCallFlowData;

  if (task.status === TASK_STATUSES.READY) {
    return (
      <FlowActionButton
        langBase={LANG_BASE}
        permission={PERMISSION}
        isActionAvailable
        labelKey="claim-to-close-vessel-call-button"
        testId="claim-to-close-vessel-call"
        variant="outlined"
        startIcon={<ContentPasteGoIcon />}
        onClick={onClaimVesselCall}
      />
    );
  }

  if (!canUserUnclaim(task.status, task.claimOwnerName)) return null;

  return (
    <>
      <UnclaimButton
        langBase={LANG_BASE}
        permission={PERMISSION}
        isActionAvailable
        onUnclaim={onUnclaimVesselCall}
      />
      <FlowActionButton
        langBase={LANG_BASE}
        permission={PERMISSION}
        isActionAvailable
        labelKey="close-vessel-call-button"
        testId="close-vessel-call"
        variant="contained"
        startIcon={<CheckCircleOutlineIcon />}
        onClick={() => openDrawer("closeVesselCallDrawer")}
      />
    </>
  );
};

export default CloseVesselCallButton;
